import {
  createFileRoute,
  useNavigate,
  useParams,
  useSearch
} from '@tanstack/react-router';
import { CheckCircle, Loader2, LogIn, XCircle } from 'lucide-react';
import * as React from 'react';

import { Button } from '~/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '~/components/ui/card';
import { useAppSettings } from '~/hooks/use-app-settings';
import { useAuth } from '~/lib/auth';

export const Route = createFileRoute('/invites/$inviteId/redeem')({
  component: RedeemInvitePage
});

function RedeemInvitePage() {
  const { inviteId } = useParams({ from: '/invites/$inviteId/redeem' });
  const search = useSearch({ from: '/invites/$inviteId/redeem' }) as {
    token?: string;
  };
  const navigate = useNavigate();
  const { redeemInvite } = useAuth();
  const { data: settings } = useAppSettings();
  const companyName = settings?.companyName ?? 'Open Dataroom';
  const [status, setStatus] = React.useState<
    'idle' | 'redeeming' | 'success' | 'error'
  >('idle');
  const [error, setError] = React.useState('');

  const token = search.token;

  const handleConfirm = React.useCallback(async () => {
    if (!token) {
      setStatus('error');
      setError('No token provided');
      return;
    }
    setStatus('redeeming');
    try {
      await redeemInvite(inviteId, token);
      window.history.replaceState(null, '', `/invites/${inviteId}/redeem`);
      setStatus('success');
      setTimeout(() => navigate({ to: '/dashboard' }), 1000);
    } catch (err: unknown) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Invite redemption failed');
    }
  }, [inviteId, token, redeemInvite, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle>
            {status === 'idle' && 'Accept Invite'}
            {status === 'redeeming' && 'Accepting invite...'}
            {status === 'success' && 'Welcome!'}
            {status === 'error' && 'Invite Failed'}
          </CardTitle>
          <CardDescription>
            {status === 'idle' &&
              `Click below to accept your invite to ${companyName}.`}
            {status === 'redeeming' &&
              `We're setting up your access to ${companyName}.`}
            {status === 'success' &&
              `You've been signed in to ${companyName} successfully.`}
            {status === 'error' && error}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center py-6">
          {status === 'redeeming' && (
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
          )}
          {status === 'success' && (
            <CheckCircle className="h-12 w-12 text-green-500" />
          )}
          {status === 'error' && (
            <XCircle className="h-12 w-12 text-destructive" />
          )}

          {status === 'idle' && (
            <Button className="mt-2" onClick={handleConfirm}>
              <LogIn className="mr-2 h-4 w-4" />
              Accept and Sign In
            </Button>
          )}

          {status === 'error' && (
            <Button className="mt-4" onClick={() => navigate({ to: '/login' })}>
              Back to Sign In
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
